/**
 * Режим презентации чата (PROP-402).
 *
 * В этом режиме агент оформляет ответ самостоятельной HTML-страницей, а мы показываем её в
 * ОСНОВНОЙ ОБЛАСТИ (маршрут `/cubus/presentation`, песочница `CbsHtmlSandbox`), в ленту же
 * кладём метку и короткое резюме. Здесь — три вещи, общие для панели и (при желании) страницы:
 *   1) директива агенту, как оформить ответ;
 *   2) разбор ответа на html-страницу и текст-резюме;
 *   3) реактивное хранилище последней страницы, которое читает маршрут.
 *
 * КАК режим доходит до агента (PROP-403): НЕ инструкцией в тексте сообщения — раннер намеренно
 * не исполняет указания из содержимого (защита от инъекций, как и для `view`). Панель шлёт
 * штатное поле кадра `present`, бэк проводит его в конверт агента (`payload.answerFormat`), а
 * системный промпт раннера велит оформить ответ HTML-страницей. Здесь — только клиентская часть:
 * разбор ответа и хранилище. Не пришла страница — деградация до обычной реплики.
 */
import Vue from 'vue'

/**
 * Достать из ответа html-страницу и текст-резюме.
 * @returns {{html: string, summary: string}|null} null — страницы в ответе нет (обычная реплика).
 */
export function extractPresentation(text) {
  if (!text) return null
  const s = String(text)
  // 1) явный блок ```html
  let m = s.match(/```(?:html)[ \t]*\n?([\s\S]*?)```/i)
  // 2) любой fenced-блок, содержимое которого похоже на HTML
  if (!m) {
    const gen = s.match(/```[a-z]*[ \t]*\n?([\s\S]*?)```/i)
    if (gen && /<\s*(html|!doctype|body|div|section|main|table|h[1-6])/i.test(gen[1])) m = gen
  }
  // 3) весь ответ — это HTML без обрамления
  if (!m && /^\s*(<!doctype|<html)/i.test(s)) return { html: s.trim(), summary: '' }
  if (!m) return null
  const html = (m[1] || '').trim()
  if (!html) return null
  const summary = s.replace(m[0], '').trim()
  return { html, summary }
}

/**
 * Показанная страница. `Vue.observable` — чтобы маршрут перерисовывался, когда панель с другого
 * экрана кладёт сюда новый html. Живёт в памяти вкладки: перезагрузка страницы её теряет.
 */
export const presentation = Vue.observable({ html: '', at: 0, id: '' })

/**
 * BUG-429: у КАЖДОГО ответа своя страница.
 *
 * Раньше хранилась одна, последняя, а ссылка «открыть снова» во всех сообщениях вела в одно и то
 * же место — то есть у старых ответов она показывала чужую страницу. Это хуже неработающей
 * ссылки: неверный ответ выглядит как верный.
 *
 * Держим их здесь, в памяти вкладки (указание владельца: в БД не хранить), и раздаём по номеру.
 * Число ограничено: страница — это десятки килобайт разметки, а разговор бывает длинным, и
 * бесконечный список тихо съел бы память вкладки. Вытесняем самые старые.
 */
const MAX_PAGES = 20
const pages = new Map()
let seq = 0

/** Положить новую страницу и показать её. Возвращает номер — с ним её потом и откроют. */
export function setPresentation(html) {
  seq += 1
  const id = String(seq)
  pages.set(id, html || '')
  while (pages.size > MAX_PAGES) pages.delete(pages.keys().next().value)
  presentation.html = html || ''
  presentation.at = Date.now()
  presentation.id = id
  return id
}

/**
 * Показать страницу по номеру. `false` — такой страницы больше нет (вкладку перезагружали либо
 * её вытеснили новые). Возвращаем именно отказ, а не «покажем последнюю»: подмена одного ответа
 * другим — это ровно тот дефект, который здесь и чинится.
 */
export function openPresentation(id) {
  const key = String(id || '')
  if (!pages.has(key)) return false
  presentation.html = pages.get(key)
  presentation.at = Date.now()
  presentation.id = key
  return true
}

/** Есть ли ещё эта страница — чтобы спрашивать, не показывая. */
export function hasPresentation(id) {
  return pages.has(String(id || ''))
}
